import { Loader2 } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MAX_ACTIVE_WEB_URLS } from '../constants/webUrl.constants';
import { useWebUrlForm } from '../hooks/useWebUrlForm';
import type { WebUrl } from '../types/webUrl.types';

interface WebUrlFormModalProps {
  webUrl: WebUrl | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WebUrlFormModal({ webUrl, open, onOpenChange }: WebUrlFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useWebUrlForm({
    webUrl,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  const title = isEditMode ? 'Edit Web URL' : 'Create Web URL';
  const description = isEditMode
    ? `Update the entry shown as "${webUrl?.title ?? ''}".`
    : 'Add a link for the mobile app slider.';

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="PRIME" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="actionLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="https://example.com" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Optional description shown with the link"
                        rows={3}
                        className="min-h-20 w-full resize-y rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/30"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="activeFrom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Active From</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          id="web-url-active-from"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="activeTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Active To</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          id="web-url-active-to"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(true)}
                        >
                          Active
                        </Button>
                        <Button
                          type="button"
                          variant={!field.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange(false)}
                        >
                          Inactive
                        </Button>
                      </div>
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      At most {MAX_ACTIVE_WEB_URLS} URLs can be active at the same time.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : isEditMode ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
